/**
 * DrillPresetsSection - Manage drill presets in Settings
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Timer, Plus, Trash2, GripVertical } from 'lucide-react';

const DEFAULT_PRESETS = [
  { name: 'Warm-up', minutes: 5, seconds: 0 },
  { name: 'Passing Drill', minutes: 10, seconds: 0 },
  { name: 'Shooting Practice', minutes: 8, seconds: 0 },
  { name: 'Small-Sided Game', minutes: 15, seconds: 0 },
  { name: 'Cool Down', minutes: 5, seconds: 0 },
];

export const DrillPresetsSection = ({ drillPresets, setDrillPresets }) => {
  const [newName, setNewName] = useState('');
  const [newMin, setNewMin] = useState(5);
  const [newSec, setNewSec] = useState(0);

  const presets = drillPresets?.length ? drillPresets : DEFAULT_PRESETS;

  const addPreset = () => {
    if (!newName.trim()) return;
    const updated = [...presets, { name: newName.trim(), minutes: parseInt(newMin) || 0, seconds: parseInt(newSec) || 0 }];
    setDrillPresets(updated);
    setNewName('');
    setNewMin(5);
    setNewSec(0);
  };

  const removePreset = (index) => {
    const updated = presets.filter((_, i) => i !== index);
    setDrillPresets(updated.length ? updated : []);
  };

  const updatePreset = (index, field, value) => {
    const updated = presets.map((p, i) => {
      if (i !== index) return p;
      if (field === 'name') return { ...p, name: value };
      if (field === 'minutes') return { ...p, minutes: Math.max(0, Math.min(99, parseInt(value) || 0)) };
      if (field === 'seconds') return { ...p, seconds: Math.max(0, Math.min(59, parseInt(value) || 0)) };
      return p;
    });
    setDrillPresets(updated);
  };

  const resetDefaults = () => {
    setDrillPresets(DEFAULT_PRESETS);
  };

  return (
    <Card data-testid="drill-presets-settings-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Timer className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Drill Presets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Set up quick-load drill presets for training sessions. These will appear in the Drill Timer for fast setup.
        </p>

        {/* Existing presets */}
        <div className="space-y-2">
          {presets.map((preset, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-100"
              data-testid={`drill-preset-${index}`}
            >
              <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
              <Input
                value={preset.name}
                onChange={(e) => updatePreset(index, 'name', e.target.value)}
                className="h-8 text-sm rounded-lg flex-1"
                data-testid={`drill-preset-name-${index}`}
              />
              <div className="flex items-center gap-1 flex-shrink-0">
                <Input
                  type="number"
                  min="0"
                  max="99"
                  value={preset.minutes}
                  onChange={(e) => updatePreset(index, 'minutes', e.target.value)}
                  className="w-14 h-8 text-center text-sm rounded-lg"
                  data-testid={`drill-preset-min-${index}`}
                />
                <span className="text-xs text-gray-400">m</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={preset.seconds}
                  onChange={(e) => updatePreset(index, 'seconds', e.target.value)}
                  className="w-14 h-8 text-center text-sm rounded-lg"
                  data-testid={`drill-preset-sec-${index}`}
                />
                <span className="text-xs text-gray-400">s</span>
              </div>
              <button
                onClick={() => removePreset(index)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                data-testid={`drill-preset-remove-${index}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* Add new preset */}
        <div className="flex items-center gap-2 p-2.5 bg-emerald-50 rounded-lg border border-emerald-100">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New preset name..."
            className="h-8 text-sm rounded-lg flex-1"
            onKeyDown={(e) => e.key === 'Enter' && addPreset()}
            data-testid="drill-preset-new-name"
          />
          <div className="flex items-center gap-1 flex-shrink-0">
            <Input
              type="number"
              min="0"
              max="99"
              value={newMin}
              onChange={(e) => setNewMin(e.target.value)}
              className="w-14 h-8 text-center text-sm rounded-lg"
              data-testid="drill-preset-new-min"
            />
            <span className="text-xs text-gray-500">m</span>
            <Input
              type="number"
              min="0"
              max="59"
              value={newSec}
              onChange={(e) => setNewSec(e.target.value)}
              className="w-14 h-8 text-center text-sm rounded-lg"
              data-testid="drill-preset-new-sec"
            />
            <span className="text-xs text-gray-500">s</span>
          </div>
          <Button
            size="sm"
            className="rounded-lg h-8 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
            onClick={addPreset}
            disabled={!newName.trim()}
            data-testid="drill-preset-add-btn"
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Reset to defaults */}
        <Button
          variant="outline"
          size="sm"
          className="text-xs text-gray-500"
          onClick={resetDefaults}
          data-testid="drill-preset-reset-defaults"
        >
          Reset to Defaults
        </Button>
      </CardContent>
    </Card>
  );
};
